import React, { useCallback, useEffect, useState } from 'react';
import { adminService, AdminUser } from '../../services/adminApi';
import { GlassButton, GlassCard, GlassInput, GlassPanel, GlassSelect, LoadingSpinner } from '../../components/GlassUI';
import {
  FaUsers, FaSearch, FaEdit, FaCheck, FaTimes,
  FaUserPlus, FaExclamationTriangle, FaKey, FaBan, FaTrash,
} from 'react-icons/fa';

const ROLE_COLORS: Record<string, string> = {
  ADMIN: 'bg-amber-100 text-amber-800',
  SUPER_ADMIN: 'bg-amber-100 text-amber-800',
  MANAGER: 'bg-indigo-100 text-indigo-700',
  STAFF: 'bg-slate-200 text-slate-600',
};

const roleChip = (role?: string) => {
  const cls = ROLE_COLORS[role || ''] || 'bg-slate-200 text-slate-600';
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{role || '—'}</span>;
};

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', email: '', phoneNumber: '', staffName: '', role: 'STAFF', password: '',
  });
  const [creating, setCreating] = useState(false);

  // Edit modal
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState({ role: 'STAFF', isActive: true, newPassword: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.listUsers();
      setUsers(data.users || []);
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Failed to load accounts' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = users.filter(
    (u) =>
      (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.role || '').toLowerCase().includes(search.toLowerCase())
  );

  const openEdit = (u: AdminUser) => {
    setEditUser(u);
    setEditForm({ role: u.role, isActive: u.isActive, newPassword: '' });
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.name || !createForm.email || !createForm.phoneNumber || !createForm.password) {
      setMsg({ type: 'err', text: 'Fill name, email, phone and password' });
      return;
    }
    setCreating(true);
    try {
      const res = await adminService.createUser(createForm);
      setMsg({ type: 'ok', text: res.message || 'Account created' });
      setShowCreate(false);
      setCreateForm({ name: '', email: '', phoneNumber: '', staffName: '', role: 'STAFF', password: '' });
      load();
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Failed to create account' });
    } finally {
      setCreating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const payload: Record<string, any> = {
        role: editForm.role,
        isActive: editForm.isActive,
      };
      if (editForm.newPassword) payload.password = editForm.newPassword;
      const res = await adminService.updateUser(editUser.id, payload);
      setMsg({ type: 'ok', text: res.message || 'Account updated' });
      setEditUser(null);
      load();
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Failed to update account' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editUser) return;
    if (!window.confirm(`Delete ${editUser.email}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await adminService.deleteUser(editUser.id);
      setMsg({ type: 'ok', text: res.message || 'Account deleted' });
      setEditUser(null);
      load();
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Failed to delete account' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className='space-y-5'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row sm:items-center gap-3'>
        <div className='flex-1'>
          <h1 className='text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3'>
            <span className='w-10 h-10 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center'>
              <FaUsers />
            </span>
            Accounts & Users
          </h1>
          <p className='text-slate-500 mt-1 text-sm'>
            Create login ids, assign roles, block accounts & reset passwords
          </p>
        </div>
        <GlassButton variant='gold' onClick={() => setShowCreate(true)} className='w-full sm:w-auto'>
          <FaUserPlus /> Create Account
        </GlassButton>
      </div>

      {msg && (
        <div
          className={`flex items-start gap-2 text-sm rounded-2xl px-4 py-3 border ${
            msg.type === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {msg.type === 'ok' ? <FaCheck className='mt-0.5 shrink-0' /> : <FaExclamationTriangle className='mt-0.5 shrink-0' />}
          <span className='break-all'>{msg.text}</span>
          <button className='ml-auto text-xs' onClick={() => setMsg(null)}>
            <FaTimes />
          </button>
        </div>
      )}

      {/* Search */}
      <div className='relative max-w-md'>
        <FaSearch className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm' />
        <GlassInput
          placeholder='Search by name, email or role...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className='!pl-11'
        />
      </div>

      {/* List */}
      {loading ? (
        <LoadingSpinner label='Loading accounts...' className='py-20' />
      ) : (
        <>
          {/* Desktop table */}
          <GlassPanel className='p-4 hidden md:block overflow-x-auto'>
            <table className='w-full text-sm text-slate-700'>
              <thead>
                <tr className='border-b border-slate-200 text-left'>
                  <th className='py-3 px-3 font-semibold'>Account</th>
                  <th className='py-3 px-3 font-semibold'>Role</th>
                  <th className='py-3 px-3 font-semibold'>Phone</th>
                  <th className='py-3 px-3 font-semibold'>Plan / Status</th>
                  <th className='py-3 px-3 font-semibold'>State</th>
                  <th className='py-3 px-3 font-semibold text-right'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className='border-b border-slate-100 hover:bg-slate-50'>
                    <td className='py-3 px-3'>
                      <p className='font-semibold text-slate-900'>{u.name}</p>
                      <p className='text-xs text-slate-500'>{u.email}</p>
                    </td>
                    <td className='py-3 px-3'>{roleChip(u.role)}</td>
                    <td className='py-3 px-3 text-slate-600'>{u.phoneNumber || '—'}</td>
                    <td className='py-3 px-3'>
                      {u.subscription?.exempt ? (
                        <span className='text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800'>
                          OWNER · no plan
                        </span>
                      ) : (
                        <>
                          <span className='font-semibold text-slate-900'>{u.subscription?.plan || '—'}</span>{' '}
                          <span className='text-xs text-slate-400'>{u.subscription?.status || ''}</span>
                        </>
                      )}
                    </td>
                    <td className='py-3 px-3'>
                      {u.isActive ? (
                        <span className='text-xs font-semibold text-emerald-600 flex items-center gap-1'>
                          <FaCheck size={10} /> Active
                        </span>
                      ) : (
                        <span className='text-xs font-semibold text-red-600 flex items-center gap-1'>
                          <FaBan size={10} /> Blocked
                        </span>
                      )}
                    </td>
                    <td className='py-3 px-3 text-right'>
                      <GlassButton variant='secondary' className='!px-3 !py-1.5 text-xs' onClick={() => openEdit(u)}>
                        <FaEdit size={11} /> Manage
                      </GlassButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <p className='text-center text-slate-400 py-10'>No accounts match "{search}"</p>
            )}
          </GlassPanel>

          {/* Mobile cards */}
          <div className='md:hidden space-y-3'>
            {filtered.map((u) => (
              <GlassCard key={u.id} className='p-4'>
                <div className='flex items-start gap-3'>
                  <div className='w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white font-bold flex items-center justify-center shrink-0'>
                    {u.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p className='font-bold text-slate-900 text-sm truncate'>{u.name}</p>
                    <p className='text-xs text-slate-500 truncate'>{u.email}</p>
                    <div className='flex items-center gap-2 mt-1.5 flex-wrap'>
                      {roleChip(u.role)}
                      <span className='text-[10px] font-semibold text-slate-500'>
                        {u.subscription?.exempt
                          ? 'OWNER · no plan'
                          : `${u.subscription?.plan || 'NO PLAN'} · ${u.subscription?.status || '—'}`}
                      </span>
                      {!u.isActive && (
                        <span className='text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700'>BLOCKED</span>
                      )}
                    </div>
                  </div>
                  <GlassButton variant='secondary' className='!px-3 !py-2 text-xs shrink-0' onClick={() => openEdit(u)}>
                    <FaEdit size={11} />
                  </GlassButton>
                </div>
              </GlassCard>
            ))}
            {filtered.length === 0 && (
              <GlassCard className='text-center py-10 text-slate-400 text-sm'>
                No accounts match "{search}"
              </GlassCard>
            )}
          </div>
        </>
      )}

      {/* ── Create account modal ── */}
      {showCreate && (
        <div className='fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4'>
          <div className='absolute inset-0 bg-slate-900/60 backdrop-blur-sm' onClick={() => setShowCreate(false)} />
          <div className='relative bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto shadow-2xl animate-slide-in-up'>
            <div className='flex items-center justify-between mb-1'>
              <h2 className='text-xl font-bold text-slate-900 flex items-center gap-2'>
                <FaUserPlus className='text-emerald-500' /> Create Account
              </h2>
              <button onClick={() => setShowCreate(false)} className='w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center'>
                <FaTimes />
              </button>
            </div>
            <p className='text-sm text-slate-500 mb-5'>
              Create a login id & password. Customer accounts sign in at apnicrm.com and start on a
              14-day free trial; platform admins get the control panel and need no plan.
            </p>
            <form onSubmit={handleCreate} className='space-y-4'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1'>Full Name *</label>
                  <GlassInput value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder='e.g. Ravi Kumar' />
                </div>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1'>Email (login id) *</label>
                  <GlassInput type='email' value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} placeholder='name@store.com' />
                </div>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1'>Phone *</label>
                  <GlassInput value={createForm.phoneNumber} onChange={(e) => setCreateForm({ ...createForm, phoneNumber: e.target.value })} placeholder='10-digit mobile' />
                </div>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1'>Short Name</label>
                  <GlassInput value={createForm.staffName} onChange={(e) => setCreateForm({ ...createForm, staffName: e.target.value })} placeholder='e.g. Ravi' />
                </div>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1'>Role</label>
                  <GlassSelect value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}>
                    <option value='STAFF'>Customer account (CRM login)</option>
                    <option value='MANAGER'>Customer manager (CRM login)</option>
                    <option value='ADMIN'>Platform admin (control panel)</option>
                  </GlassSelect>
                </div>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1'>Password *</label>
                  <GlassInput value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder='min 6 characters' />
                </div>
              </div>
              <div className='flex gap-3 pt-2'>
                <GlassButton type='submit' variant='gold' className='flex-1' disabled={creating}>
                  {creating ? 'Creating...' : 'Create & Get Login'}
                </GlassButton>
                <GlassButton variant='secondary' type='button' onClick={() => setShowCreate(false)}>
                  Cancel
                </GlassButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Manage account modal ── */}
      {editUser && (
        <div className='fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4'>
          <div className='absolute inset-0 bg-slate-900/60 backdrop-blur-sm' onClick={() => setEditUser(null)} />
          <div className='relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto shadow-2xl animate-slide-in-up'>
            <div className='flex items-center justify-between mb-1'>
              <h2 className='text-xl font-bold text-slate-900 flex items-center gap-2'>
                <FaEdit className='text-indigo-500' /> Manage Account
              </h2>
              <button onClick={() => setEditUser(null)} className='w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center'>
                <FaTimes />
              </button>
            </div>
            <p className='text-sm text-slate-600 mb-5 break-all'>
              {editUser.name} · <span className='text-slate-400'>{editUser.email}</span>
            </p>

            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-1'>Role</label>
                <GlassSelect value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                  <option value='STAFF'>Customer account (CRM login)</option>
                  <option value='MANAGER'>Customer manager (CRM login)</option>
                  <option value='ADMIN'>Platform admin (control panel)</option>
                </GlassSelect>
              </div>
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-1'>
                  <FaKey className='inline mr-1 text-amber-500' /> Reset Password (leave blank to keep)
                </label>
                <GlassInput value={editForm.newPassword} onChange={(e) => setEditForm({ ...editForm, newPassword: e.target.value })} placeholder='New password' />
              </div>
              <div className='flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3'>
                <div>
                  <p className='text-sm font-semibold text-slate-800'>Account active</p>
                  <p className='text-xs text-slate-500'>Blocked accounts cannot log in</p>
                </div>
                <button
                  onClick={() => setEditForm({ ...editForm, isActive: !editForm.isActive })}
                  className={`relative w-12 h-7 rounded-full transition-colors ${editForm.isActive ? 'bg-emerald-500' : 'bg-slate-300'}`}
                  aria-label='Toggle active'
                >
                  <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${editForm.isActive ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>
              <div className='flex gap-3 pt-1'>
                <GlassButton variant='primary' className='flex-1' onClick={handleSaveEdit} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </GlassButton>
                <GlassButton variant='secondary' onClick={() => setEditUser(null)}>
                  Cancel
                </GlassButton>
              </div>

              {!editUser.subscription?.exempt && editUser.role !== 'ADMIN' && editUser.role !== 'SUPER_ADMIN' && (
                <div className='pt-3 mt-1 border-t border-slate-100'>
                  <GlassButton
                    variant='danger'
                    className='w-full !py-2 text-xs'
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    <FaTrash size={11} /> {deleting ? 'Deleting...' : 'Delete account'}
                  </GlassButton>
                  <p className='text-[11px] text-slate-400 mt-1.5 text-center'>
                    Accounts that own transaction records can only be blocked, not deleted.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
